/**
 * Worker Sorting Utility
 *
 * Provides functions to sort worker/employee lists based on user settings.
 * Supports automatic sorting by various fields and custom manual ordering.
 */

import logger from "../core/utils/logger";

/**
 * Sort workers based on settings
 * @param {Array} workers - Array of worker/employee objects
 * @param {Array} users - Array of user data (for detailed user info)
 * @param {Object} settings - Sorting settings from Redux
 * @returns {Array} - Sorted array of workers
 */
export const sortWorkers = (workers, users, settings) => {
    console.log("🔀 sortWorkers called with:", {
        workersCount: workers?.length,
        usersCount: users?.length,
        settings: settings,
        customWorkerOrder: settings?.customWorkerOrder,
        customWorkerOrderType: typeof settings?.customWorkerOrder
    });

    if (!workers || workers.length === 0) {
        console.log("⚠️ No workers to sort");
        return workers;
    }

    // Check if custom order exists (handle both string and array formats)
    const hasCustomOrder = settings?.customWorkerOrder &&
        (typeof settings.customWorkerOrder === 'string'
            ? settings.customWorkerOrder !== '[]' && settings.customWorkerOrder !== '' && settings.customWorkerOrder !== 'null'
            : Array.isArray(settings.customWorkerOrder) && settings.customWorkerOrder.length > 0
        );

    if (hasCustomOrder) {
        console.log("🎯 Using custom worker order");
        return sortByCustomOrder(workers, settings.customWorkerOrder);
    }

    // Otherwise, use automatic sorting
    const sortBy = settings?.workerSortBy || 'Name';
    const sortOrder = settings?.workerSortOrder || 'Asc';
    console.log(`📊 Using automatic sorting: ${sortBy} (${sortOrder})`);

    return sortByField(workers, users, sortBy, sortOrder);
};

/**
 * Sort workers by a specific field
 * @param {Array} workers - Array of worker/employee objects
 * @param {Array} users - Array of user data
 * @param {string} sortBy - Field to sort by (Name, FirstName, Surname, Position)
 * @param {string} sortOrder - Sort order (Asc or Desc)
 * @returns {Array} - Sorted array
 */
export const sortByField = (workers, users, sortBy, sortOrder) => {
    const sorted = [...workers].sort((a, b) => {
        let valueA = getCompareValue(a, users, sortBy);
        let valueB = getCompareValue(b, users, sortBy);

        // Handle null/undefined values - push them to the end
        if (!valueA && !valueB) return 0;
        if (!valueA) return 1;
        if (!valueB) return -1;

        // Perform case-insensitive string comparison
        const result = valueA.toString().toLowerCase().localeCompare(
            valueB.toString().toLowerCase(),
            undefined,
            { numeric: true, sensitivity: 'base' }
        );

        return sortOrder === 'Desc' ? -result : result;
    });

    logger.log(`Sorted ${workers.length} workers by ${sortBy} (${sortOrder})`);
    return sorted;
};

/**
 * Get the comparison value for a worker based on sort field
 * @param {Object} worker - Worker/employee object
 * @param {Array} users - Array of user data
 * @param {string} sortBy - Field to sort by
 * @returns {string} - Value to use for comparison
 */
const getCompareValue = (worker, users, sortBy) => {
    // Try to find the full user data
    const user = users?.find(u =>
        u.email === worker.email ||
        u.email === worker.userName ||
        u.id === worker.userId ||
        u.id === worker.id
    );

    switch (sortBy) {
        case 'FirstName':
            return user?.firstName || worker.firstName || worker.name || '';

        case 'Surname':
            return user?.surname || worker.surname || worker.name || '';

        case 'Position':
            return user?.position || worker.position || '';

        case 'Name':
        default:
            // Construct full name if available
            if (user?.firstName && user?.surname) {
                const title = user.title ? `${user.title} ` : '';
                return `${title}${user.firstName} ${user.surname}`;
            }
            return worker.name || worker.userName || user?.name || '';
    }
};

/**
 * Sort workers by custom order (manual drag-and-drop)
 * @param {Array} workers - Array of worker/employee objects
 * @param {string|Array} customOrder - JSON string or array of user IDs in desired order
 * @returns {Array} - Sorted array
 */
export const sortByCustomOrder = (workers, customOrder) => {
    try {
        console.log("📝 sortByCustomOrder called with customOrder:", customOrder);

        // Parse custom order if it's a string
        const orderArray = typeof customOrder === 'string'
            ? JSON.parse(customOrder)
            : customOrder;

        console.log("📋 Parsed order array:", orderArray);

        if (!Array.isArray(orderArray) || orderArray.length === 0) {
            console.warn("⚠️ Invalid custom order, falling back to original order");
            logger.warn("Invalid custom order, falling back to original order");
            return workers;
        }

        // Create a map of position for each user ID
        // Convert all IDs to strings to ensure consistent comparison
        const positionMap = new Map();
        orderArray.forEach((id, index) => {
            positionMap.set(String(id), index);
        });

        console.log("🗺️ Position map:", Array.from(positionMap.entries()));

        // Sort workers based on their position in the custom order
        const sorted = [...workers].sort((a, b) => {
            // Convert IDs to strings for consistent comparison
            const idA = String(a.userId || a.id);
            const idB = String(b.userId || b.id);

            console.log(`🔍 Comparing: idA="${idA}" vs idB="${idB}"`);

            const posA = positionMap.has(idA) ? positionMap.get(idA) : Infinity;
            const posB = positionMap.has(idB) ? positionMap.get(idB) : Infinity;

            console.log(`   Positions: posA=${posA}, posB=${posB}`);

            return posA - posB;
        });

        console.log("✅ Sorted workers:", sorted.map(w => ({ id: w.userId || w.id, name: w.name || w.userName })));
        logger.log(`Sorted ${workers.length} workers by custom order`);
        return sorted;
    } catch (error) {
        console.error("❌ Error applying custom order:", error);
        logger.error("Error applying custom order:", error);
        return workers; // Return original order on error
    }
};

/**
 * Get sort options for UI dropdowns
 */
export const getSortOptions = () => {
    return {
        sortByOptions: [
            { value: 'Name', label: 'Name' },
            { value: 'FirstName', label: 'First Name' },
            { value: 'Surname', label: 'Surname' },
            { value: 'Position', label: 'Position' }
        ],
        sortOrderOptions: [
            { value: 'Asc', label: 'Ascending' },
            { value: 'Desc', label: 'Descending' }
        ]
    };
};
